 /*  popup-a11y-remediation.js
 *  Accessibility remediation for third-party popups (Klaviyo signup + GiftBee free gift).
 *  Adds dialog semantics, an accessible name, an SR announcement on appearance,
 *  focus move / trap / restore, and a guaranteed Escape close.
 *  Load once, as late as possible, with `defer`. Safe to load on every page.
 */
(function () {
  'use strict';
  if (window.__a11yPopupPatch) { return; }
  window.__a11yPopupPatch = true;

  /* ------------------------------------------------------------------ *
   * CONFIG — one entry per popup. Add new vendors here only.
   * NOTE: #giftbee-loaded is an empty display:none marker, NOT the dialog.
   * ------------------------------------------------------------------ */
  var PATTERNS = [
    {
      id: 'klaviyo-signup',                                   // "Unlock 10% off"
      rootSelector: '[data-kl-scroll-locking-modal="true"]',
      requiresDescendant: '.klaviyo-form, [data-testid="POPUP"]',
      headingSelector: '.klaviyo-form h1, .klaviyo-form h2, .klaviyo-form h3',
      imageTitleSelector: '[data-testid="POPUP"] img[alt]:not([alt=""])',
      closeSelector: 'button[aria-label*="close" i], [aria-label="Close dialog"]',
      fallbackLabel: 'Email sign up offer'
    },
    {
      id: 'giftbee-free-gift',                                // "Surprise free gift"
      rootSelector: '#mbcFreeGiftUi .mbc-modal, .mbc-modal, .mbc-bundles-modal',
      headingSelector: '.mbc-modal__title, .mbc-gift-modal__group-title, h1, h2, h3',
      imageTitleSelector: '.mbc-modal__content img[alt]:not([alt=""])',
      closeSelector: '.mbc-modal__close, button[aria-label*="close" i]',
      overlaySelector: '.mbc-modal__overlay',
      fallbackLabel: 'Surprise free gift'
    }
  ];

  var TRIGGER_SELECTOR = '.mbc-gift-bubble';   // floating bubble that opens the gift modal

  var FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
    'textarea:not([disabled])', 'iframe', 'audio[controls]', 'video[controls]',
    '[contenteditable="true"]', '[tabindex]:not([tabindex^="-"])'
  ].join(',');

  var SKIP_BG      = /^(script|style|link|meta|template|noscript)$/i;
  var IGNORE_ALT   = /(logo|icon|badge|arrow|close|spacer|divider|brand)/i;
  var GENERIC_NAME = /^(popup|popup form|dialog|modal|form|close)$/i;
  var SR_ONLY = 'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;' +
                'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0;';

  var openStack = [];
  function norm(s) { return (s || '').replace(/\s+/g, ' ').trim(); }
  function uid(p)  { return p + '-' + Math.random().toString(36).slice(2, 8); }

  /* Remember the last element focused OUTSIDE a managed dialog, so focus can be
     returned there when the popup closes (WCAG 2.4.3 Focus Order). */
  var lastExternalFocus = null;
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t && t.closest && !t.closest('[data-a11y-dialog]') && t !== document.body) lastExternalFocus = t;
  }, true);

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    var r = el.getBoundingClientRect();
    return r.width > 1 && r.height > 1;
  }

  function focusables(root) {
    return Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
      if (el.getAttribute('aria-hidden') === 'true' || el.closest('[inert]')) return false;
      var r = el.getBoundingClientRect();
      return (r.width > 0 || r.height > 0) && getComputedStyle(el).visibility !== 'hidden';
    });
  }

  /* ------------------------------------------------------------------ *
   * Announcer — informs the user that a popup has appeared.
   * ------------------------------------------------------------------ */
  var announcer = (function () {
    var live = document.getElementById('a11y-popup-announcer');
    if (!live) {
      live = document.createElement('div');
      live.id = 'a11y-popup-announcer';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      live.style.cssText = SR_ONLY;
      document.body.appendChild(live);
    }
    return {
      node: live,
      say: function (msg) {                     // clear first so repeats re-announce
        live.textContent = '';
        setTimeout(function () { live.textContent = msg; }, 60);
      }
    };
  })();

  if (!document.getElementById('a11y-popup-style')) {
    var st = document.createElement('style');
    st.id = 'a11y-popup-style';
    st.textContent =
      '[data-a11y-dialog]{outline:none!important}' +
      '[data-a11y-dialog] :focus-visible{outline:3px solid #1a73e8!important;outline-offset:2px!important}' +
      '[data-a11y-trigger]:focus-visible{outline:3px solid #1a73e8!important;outline-offset:2px!important}';
    document.head.appendChild(st);
  }

  /* ------------------------------------------------------------------ *
   * Accessible name. Deliberately does NOT inject nodes into the vendor
   * subtree — their frameworks re-render and wipe injected children
   * (verified: an injected <h2> was removed, leaving a dangling
   * aria-labelledby and therefore no name at all).
   * ------------------------------------------------------------------ */
  function bestImageTitle(root, sel) {
    var imgs = Array.prototype.slice.call(root.querySelectorAll(sel || 'img[alt]:not([alt=""])'));
    var best = null, bestScore = -1;
    imgs.forEach(function (img) {
      var alt = norm(img.getAttribute('alt'));
      if (!alt || IGNORE_ALT.test(alt)) return;       // skip the logo, badges, icons
      var r = img.getBoundingClientRect();
      var score = alt.length + Math.round((r.width * r.height) / 5000);
      if (score > bestScore) { bestScore = score; best = { el: img, text: alt }; }
    });
    return best;
  }

  function applySemantics(root, pattern) {
    if (!/^(dialog|alertdialog)$/.test(root.getAttribute('role') || '')) root.setAttribute('role', 'dialog');
    if (root.getAttribute('aria-modal') !== 'true') root.setAttribute('aria-modal', 'true');
    if (root.getAttribute('tabindex') !== '-1')     root.setAttribute('tabindex', '-1');

    var label = '';
    var heading = pattern.headingSelector ? root.querySelector(pattern.headingSelector) : null;

    if (heading && isVisible(heading)) {
      label = norm(heading.textContent);
      if (!/^H[1-6]$/.test(heading.tagName) && !heading.getAttribute('role')) {
        heading.setAttribute('role', 'heading');      // e.g. .mbc-modal__title is a plain div
        heading.setAttribute('aria-level', '2');
      }
      if (!heading.id) heading.id = uid('a11y-dlg-title');
      root.setAttribute('aria-labelledby', heading.id);
      root.removeAttribute('aria-label');
    } else {
      // Klaviyo bakes the offer copy into an image. Promote that image to a
      // heading so its alt text becomes a navigable, announced title.
      var imgTitle = bestImageTitle(root, pattern.imageTitleSelector);
      if (imgTitle) {
        label = imgTitle.text;
        if (!imgTitle.el.getAttribute('role')) {
          // imgTitle.el.setAttribute('role', 'heading');
          // imgTitle.el.setAttribute('aria-level', '2');
        }
      }
      if (!label) {
        var existing = norm(root.getAttribute('aria-label'));
        if (existing && !GENERIC_NAME.test(existing)) label = existing;  // reject "POPUP Form"
      }
      if (!label) label = pattern.fallbackLabel;
      if (norm(root.getAttribute('aria-label')) !== label) root.setAttribute('aria-label', label);
      var lb = root.getAttribute('aria-labelledby');
      if (lb && !document.getElementById(lb)) root.removeAttribute('aria-labelledby');
    }

    fixCloseButton(root, pattern);
    return label;
  }

  function fixCloseButton(root, pattern) {
    var btn = pattern.closeSelector ? root.querySelector(pattern.closeSelector) : null;
    if (!btn) {
      btn = Array.prototype.slice.call(root.querySelectorAll('button,[role="button"],div,span'))
        .filter(function (e) { return /^(x|✕|×|close)$/i.test(norm(e.textContent)); })[0] || null;
    }
    if (!btn) { root.__a11yClose = null; return null; }

    btn.setAttribute('data-a11y-close', '');
    if (btn.tagName !== 'BUTTON' && btn.getAttribute('role') !== 'button') {
      btn.setAttribute('role', 'button');                       // GiftBee uses a bare <div>
      if (!btn.hasAttribute('tabindex')) btn.setAttribute('tabindex', '0');
      if (!btn.__a11yKeyBound) {
        btn.__a11yKeyBound = true;
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); btn.click(); }
        });
      }
    }
    // A typeless <button> inside a <form> defaults to submit, so activating the
    // close control would submit the email form instead of dismissing the popup.
    if (btn.tagName === 'BUTTON' && btn.type === 'submit') btn.setAttribute('type', 'button');

    // Only supply a name if there is none, or if it is a bare glyph. An existing
    // descriptive label (e.g. from another a11y layer) is left alone.
    var accName = norm(btn.getAttribute('aria-label') || btn.textContent);
    if (!accName || /^(x|✕|×)$/i.test(accName)) btn.setAttribute('aria-label', 'Close dialog');

    root.__a11yClose = btn;
    return btn;
  }

  /* ------------------------------------------------------------------ *
   * Background isolation: remove the rest of the page from the
   * accessibility tree and the tab order while a modal is open.
   * ------------------------------------------------------------------ */
  function hideBackground(root) {
    var touched = [], node = root;
    while (node && node.parentElement && node !== document.body) {
      var parent = node.parentElement;
      Array.prototype.forEach.call(parent.children, function (sib) {
        if (sib === node || SKIP_BG.test(sib.tagName) || sib === announcer.node) return;
        if (sib.hasAttribute('data-a11y-bg-hidden')) return;   // already hidden by an outer dialog
        touched.push({ el: sib, aria: sib.getAttribute('aria-hidden'), inert: sib.hasAttribute('inert') });
        sib.setAttribute('data-a11y-bg-hidden', '');
        sib.setAttribute('aria-hidden', 'true');
        if ('inert' in sib) sib.inert = true;
      });
      node = parent;
    }
    return touched;
  }

  function restoreBackground(touched) {
    touched.forEach(function (rec) {
      rec.el.removeAttribute('data-a11y-bg-hidden');
      if (rec.aria === null) rec.el.removeAttribute('aria-hidden');
      else rec.el.setAttribute('aria-hidden', rec.aria);
      if ('inert' in rec.el) rec.el.inert = rec.inert;
    });
  }

  function fallbackFocusTarget() {
    var el = document.querySelector('main, #root, [role="main"]');
    if (!el) return null;
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    return el;
  }

  /* ------------------------------------------------------------------ *
   * Open / close lifecycle
   * ------------------------------------------------------------------ */
  function openDialog(root, pattern) {
    if (root.__a11yOpen) return;
    root.__a11yOpen = true;
    root.setAttribute('data-a11y-dialog', pattern.id);

    var name = applySemantics(root, pattern);

    var ret = (root.__a11yTrigger && root.__a11yTrigger.isConnected) ? root.__a11yTrigger : null;
    if (!ret && lastExternalFocus && lastExternalFocus.isConnected && !root.contains(lastExternalFocus)) {
      ret = lastExternalFocus;
    }

    var state = { root: root, pattern: pattern, name: name, returnFocus: ret, touched: hideBackground(root) };

    state.onKeydown = function (e) {
      if (openStack[openStack.length - 1] !== state) return;      // only the topmost dialog reacts
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation(); e.preventDefault();
        requestClose(state);
        return;
      }
      if (e.key !== 'Tab') return;
      var list = focusables(root);
      if (!list.length) { e.preventDefault(); root.focus(); return; }
      var first = list[0], last = list[list.length - 1], active = document.activeElement;
      if (!root.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && (active === first || active === root)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last)                 { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', state.onKeydown, true); // capture: beat the vendor handler

    // Backstop for anything outside the dialog that tries to take focus.
    state.onFocusIn = function (e) {
      if (openStack[openStack.length - 1] !== state) return;
      if (!root.contains(e.target) && e.target !== announcer.node) {
        e.stopPropagation();
        var list = focusables(root);
        (list[0] || root).focus();
      }
    };
    document.addEventListener('focusin', state.onFocusIn, true);

    openStack.push(state);

    // Focus the dialog container itself so the SR announces role + name + content.
    // Deferred twice because Klaviyo focuses a decorative <img> of its own.
    var settle = function () {
      if (!root.__a11yOpen) return;
      if (root.contains(document.activeElement) &&
          document.activeElement !== root &&
          document.activeElement.tagName !== 'IMG') return;
      try { root.focus({ preventScroll: true }); } catch (err) { root.focus(); }
    };
    requestAnimationFrame(function () { setTimeout(settle, 50); setTimeout(settle, 300); });

    announcer.say(name + '. Dialog opened. Press Escape to close.');
    return state;
  }

  /* Escape must always work. Ask the vendor to close first; if the popup is still
     on screen shortly after, hide it ourselves so the keyboard user is never
     trapped (WCAG 2.1.2 No Keyboard Trap). */
  function requestClose(state) {
    var root = state.root;
    if (root.__a11yClose) { try { root.__a11yClose.click(); } catch (e) {} }
    setTimeout(function () {
      if (!root.__a11yOpen) return;
      if (!isVisible(root)) { scan(); return; }
      forceHide(root, state.pattern);
      closeDialog(state, true);
    }, 250);
  }

  function forceHide(root, pattern) {
    root.setAttribute('hidden', '');
    root.style.setProperty('display', 'none', 'important');
    if (pattern.overlaySelector) {
      var scope = root.parentElement || document;
      Array.prototype.forEach.call(scope.querySelectorAll(pattern.overlaySelector), function (o) {
        o.style.setProperty('display', 'none', 'important');
      });
    }
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    document.body.classList.remove('klaviyo-prevent-body-scrolling');
  }

  function closeDialog(state, announce) {
    var i = openStack.indexOf(state);
    if (i > -1) openStack.splice(i, 1);
    document.removeEventListener('keydown', state.onKeydown, true);
    document.removeEventListener('focusin', state.onFocusIn, true);
    restoreBackground(state.touched);
    state.root.__a11yOpen = false;
    state.root.removeAttribute('data-a11y-dialog');

    var target = (state.returnFocus && state.returnFocus.isConnected) ? state.returnFocus : fallbackFocusTarget();
    if (target) { try { target.focus({ preventScroll: true }); } catch (e) {} }
    if (announce !== false) announcer.say(state.name + ' dialog closed.');
  }

  /* ------------------------------------------------------------------ *
   * The floating gift bubble that opens the free-gift modal
   * ------------------------------------------------------------------ */
  function fixTrigger(el) {
    el.setAttribute('data-a11y-trigger', '');
    if (el.tagName !== 'BUTTON' && el.getAttribute('role') !== 'button') {
      el.setAttribute('role', 'button');
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    }
    if (!norm(el.getAttribute('aria-label')) && !norm(el.textContent)) {
      el.setAttribute('aria-label', 'Open free gift offers');
    }
    el.setAttribute('aria-haspopup', 'dialog');
    var badge = el.querySelector('.mbc-gift-bubble__badge');
    if (badge && !badge.getAttribute('aria-label')) {
      var n = norm(badge.textContent);
      if (n) badge.setAttribute('aria-label', n + ' free gift' + (n === '1' ? '' : 's') + ' available');
    }
    if (!el.__a11yBound) {
      el.__a11yBound = true;
      el.addEventListener('keydown', function (e) {
        if (el.tagName !== 'BUTTON' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); el.click(); }
      });
      el.addEventListener('click', function () { window.__a11yLastTrigger = el; });
    }
  }

  /* ------------------------------------------------------------------ *
   * Scanner — runs on mutation plus a safety interval, and re-asserts
   * semantics on every pass so vendor re-renders cannot undo the fix.
   * ------------------------------------------------------------------ */
  function findRoots(pattern) {
    var out = [];
    Array.prototype.forEach.call(document.querySelectorAll(pattern.rootSelector), function (el) {
      if (pattern.requiresDescendant && !el.querySelector(pattern.requiresDescendant)) return;
      if (out.some(function (o) { return o.contains(el); })) return;   // keep the outermost match
      out = out.filter(function (o) { return !el.contains(o); });
      out.push(el);
    });
    return out;
  }

  function scan() {
    PATTERNS.forEach(function (pattern) {
      findRoots(pattern).forEach(function (root) {
        if (!isVisible(root)) return;
        if (root.__a11yOpen) { applySemantics(root, pattern); return; }   // self-heal
        if (window.__a11yLastTrigger) {
          root.__a11yTrigger = window.__a11yLastTrigger;
          window.__a11yLastTrigger = null;
        }
        openDialog(root, pattern);
      });
    });
    openStack.slice().forEach(function (state) {                          // vendor closed it
      if (!state.root.isConnected || !isVisible(state.root)) closeDialog(state, true);
    });
    Array.prototype.forEach.call(document.querySelectorAll(TRIGGER_SELECTOR), fixTrigger);
  }

  var queued = false;
  var mo = new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; scan(); });
  });
  mo.observe(document.documentElement, {
    childList: true, subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'open', 'role', 'aria-label']
  });
  var poll = setInterval(scan, 1000);   // safety net for CSS-only show/hide

  window.__a11yPopupPatchAPI = {
    scan: scan, openStack: openStack, patterns: PATTERNS,
    destroy: function () {
      mo.disconnect(); clearInterval(poll);
      openStack.slice().forEach(function (s) { closeDialog(s, false); });
      window.__a11yPopupPatch = false;
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  scan();
})();

